import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';
import { selectNShowcase } from '@/store/slices/userPreferences';

import { PageShell, InsightsPanel } from '../../components';
import { ROUTE } from '../../routes';
import { useMockEndpoint } from '../../hooks/useMockEndpoint';
import { operationsData } from '../../mock-data/operations';
import { useOperationsOverview } from './useOperationsOverview';
import { useOperationsPromotions } from './useOperationsPromotions';
import { useOperationsLiveEvents } from './useOperationsLiveEvents';
import { Promotions, AutomatedStats, OperationsFeed } from './components';
import { OpsPage, Section, OpsSectionTitle, PanelGroup } from './operations.styles';

export type OperationsProps = {
  styleText?: string;
  className?: string;
};

/**
 * Operations root page. Shares the acquisition skeleton: the insights panel
 * (metric-gauges variant: Conversion / Monetization / Retention), the
 * promotions browser (the acquisition Campaigns table reused, with operations
 * benefit cards), the Automated Operations stats and a live Operations feed
 * polled from the CRM Events service. Center content only (the layout owns the
 * title).
 */
const Operations = ({ styleText = '', className = '' }: OperationsProps): React.ReactElement => {
  const { t } = useTranslation();
  const data = operationsData;
  const navigate = useNavigate();

  // The insights gauges are still mock (no API); the automated-operations totals are
  // now wired to the CRM Events `/overview` endpoint (per active brand), same
  // `{ ..., loading }` shape the mock endpoint had.
  const insightsEp = useMockEndpoint(data.insights);
  const automated = useOperationsOverview(data.automated.stats);
  // Promotions browser wired to the CRM Events `/campaigns` items (per active brand).
  // Empty in test (no operations data), so the browser shows its empty states.
  const promotions = useOperationsPromotions();
  // Live Operations feed wired to the CRM Events `/live-events` endpoint (per active
  // brand, gently polled). Empty in test, so the feed shows its empty state.
  const liveEvents = useOperationsLiveEvents();
  // The Newton showcase shows no scores, so the insights gauges are left out.
  const newtonShowcase = useAppSelector(selectNShowcase);

  // The insights gauges draw in once their data arrives: `revealed` flips
  // false→true a frame after the endpoint resolves (a load-keyed mount animation).
  const [insightsRevealed, setInsightsRevealed] = React.useState(false);
  React.useEffect(() => {
    if (insightsEp.loading) {
      setInsightsRevealed(false);
      return;
    }
    const id = requestAnimationFrame(() => setInsightsRevealed(true));
    return () => cancelAnimationFrame(id);
  }, [insightsEp.loading]);

  const onOpenPromotion = React.useCallback(
    (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      const id = event.currentTarget.dataset.id;
      if (id) navigate(ROUTE.operationsPromotion(id));
    },
    [navigate],
  );

  return (
    <PageShell styleText={styleText} className={className}>
      <OpsPage>
        {newtonShowcase ? null : (
          <InsightsPanel summary={data.insights.summary} metrics={data.insights.metrics} animate={insightsRevealed} loading={insightsEp.loading} />
        )}

        <Section>
          <OpsSectionTitle title={t('Promotions for players')} />
          <Promotions
            tabs={data.promotions.tabs}
            filters={data.promotions.filters}
            pool={promotions.pool}
            onOpenPromotion={onOpenPromotion}
          />
        </Section>

        <Section>
          <OpsSectionTitle title={t('Automated Operations')} />
          <PanelGroup>
            <AutomatedStats stats={automated.stats} loading={automated.loading} />
            <OperationsFeed rows={liveEvents.rows} loading={liveEvents.loading} />
          </PanelGroup>
        </Section>
      </OpsPage>
    </PageShell>
  );
};

export default Operations;
